// DEPRECATED (2026-07-19): pwa/src/screens/ is scheduled for deletion in STEP-M8-V4-LEGACY-SCREENS.
// Active screens are in pwa/src/v4/screens/ — use those instead.
import { useState, useEffect, useCallback, type ReactNode } from "react";
import {
  fetchDispatches,
  fetchDispatchDetail,
  fetchDispatchTimeline,
  cancelDispatch,
} from "../api/client.js";
import type {
  DispatchItem,
  DispatchDetail,
  DispatchStats,
  DispatchTimelineResponse,
  TimelineEvent,
  ChildTimelineEvent,
  NavigationIntent,
} from "../api/types.js";

function statusChipClass(status: string): string {
  if (status === "running") return "chip accent";
  if (status === "complete" || status === "success") return "chip ok";
  if (status === "failed" || status === "error") return "chip danger";
  return "chip";
}

function durLabel(ms: number | null): string {
  if (!ms) return "";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function fmtCost(usd: number | null | undefined): string {
  if (usd == null) return "—";
  return `$${usd.toFixed(2)}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function elapsedMins(createdAt: string): number {
  return (Date.now() - new Date(createdAt).getTime()) / 60000;
}

function elapsedLabel(createdAt: string): string {
  const min = elapsedMins(createdAt);
  if (min < 1) return `${Math.floor(min * 60)}s`;
  if (min < 60) return `${Math.floor(min)}m`;
  return `${Math.floor(min / 60)}h ${Math.floor(min % 60)}m`;
}

function isStalled(d: DispatchItem): boolean {
  if (d.status !== "running") return false;
  if (!d.estimated_duration_min) return false;
  return elapsedMins(d.created_at) > d.estimated_duration_min * 2;
}

/** Running > 6h with no activity — zombie dispatch the watchdog missed. */
function isZombie(d: DispatchItem): boolean {
  if (d.status !== "running") return false;
  const activityAt = d.last_activity_at ?? d.updated_at;
  const minsIdle = (Date.now() - new Date(activityAt).getTime()) / 60000;
  return minsIdle > 360; // 6 hours
}

function StatsBar({ stats }: { stats: DispatchStats }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: "8px 20px 4px",
        flexWrap: "wrap",
      }}
    >
      {stats.running > 0 && (
        <span className="mono" style={{ fontSize: 11 }}>
          <span style={{ color: "var(--accent)", fontWeight: 700 }}>
            {stats.running}
          </span>
          <span style={{ color: "var(--text-dim)" }}> running</span>
        </span>
      )}
      <span className="mono" style={{ fontSize: 11 }}>
        <span style={{ color: "var(--ok)" }}>{stats.success}</span>
        <span style={{ color: "var(--text-dim)" }}> done</span>
      </span>
      {stats.failed > 0 && (
        <span className="mono" style={{ fontSize: 11 }}>
          <span style={{ color: "var(--danger)" }}>{stats.failed}</span>
          <span style={{ color: "var(--text-dim)" }}> failed</span>
        </span>
      )}
      <span
        className="mono"
        style={{ fontSize: 11, color: "var(--text-dim)" }}
      >
        {stats.total} total (24h)
      </span>
    </div>
  );
}

function PersonaBreakdown({ dispatches }: { dispatches: DispatchItem[] }) {
  if (dispatches.length === 0) return null;
  const byPersona = new Map<
    string,
    { running: number; total: number; cost: number }
  >();
  for (const d of dispatches) {
    const key = d.persona_id ?? "unknown";
    const cur = byPersona.get(key) ?? { running: 0, total: 0, cost: 0 };
    cur.total++;
    if (d.status === "running") cur.running++;
    cur.cost += d.cost_usd ?? 0;
    byPersona.set(key, cur);
  }
  const sorted = [...byPersona.entries()].sort((a, b) => b[1].total - a[1].total);
  return (
    <div style={{ padding: "0 20px 12px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {sorted.map(([persona, stats]) => (
          <div
            key={persona}
            style={{
              padding: "4px 10px",
              background: "var(--bg-elev)",
              borderRadius: 8,
              border: "1px solid var(--border-soft)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              className="mono"
              style={{ fontSize: 10, color: "var(--accent)", fontWeight: 700 }}
            >
              {persona.toUpperCase()}
            </span>
            {stats.running > 0 && (
              <div className="live-dot" style={{ width: 5, height: 5 }} />
            )}
            <span
              className="mono"
              style={{ fontSize: 10, color: "var(--text-dim)" }}
            >
              {stats.total}
              {stats.cost > 0 ? ` · $${stats.cost.toFixed(2)}` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DispatchCard({
  d,
  onTap,
  onDismiss,
  depth = 0,
}: {
  d: DispatchItem;
  onTap: () => void;
  onDismiss?: (id: string) => void;
  /** Nesting depth in the dispatch tree — 0 = root. Indents 16px per level. */
  depth?: number;
}) {
  const isRunning = d.status === "running";
  const stalled = isStalled(d);
  const zombie = isZombie(d);
  const tintClass = stalled || zombie
    ? " card-tinted-danger"
    : isRunning
      ? " card-tinted-accent"
      : "";
  return (
    <button
      className={`card${tintClass}`}
      style={{
        display: "block",
        width: `calc(100% - ${40 + depth * 16}px)`,
        marginLeft: depth > 0 ? 20 + depth * 16 : undefined,
        textAlign: "left",
        cursor: "pointer",
      }}
      onClick={onTap}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
      >
        {isRunning && (
          <div className="live-dot" style={{ flexShrink: 0 }} />
        )}
        <span
          className="mono"
          style={{ color: "var(--accent)", fontSize: 10, fontWeight: 700 }}
        >
          {(d.persona_id ?? "unknown").toUpperCase()}
        </span>
        <span style={{ flex: 1 }} />
        {zombie && (
          <span className="chip danger" style={{ fontSize: 9 }}>
            ZOMBIE
          </span>
        )}
        {stalled && !zombie && (
          <span className="chip danger" style={{ fontSize: 9 }}>
            STALLED
          </span>
        )}
        {(zombie || stalled) && onDismiss && (
          <button
            className="chip"
            style={{
              fontSize: 9,
              cursor: "pointer",
              background: "var(--danger-soft)",
              color: "var(--danger)",
              border: "1px solid var(--danger)",
              borderRadius: 4,
              padding: "1px 6px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(d.job_id);
            }}
          >
            DISMISS
          </button>
        )}
        <span className={statusChipClass(d.status)}>{d.status}</span>
      </div>

      <div
        className="mono"
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          marginBottom: 4,
          wordBreak: "break-all",
        }}
      >
        {d.job_id}
      </div>

      {d.session_id && (
        <div
          className="mono"
          style={{ fontSize: 10, color: "var(--text-dim)", marginBottom: 4 }}
        >
          session: {d.session_id}
        </div>
      )}

      {(d.step_ids ?? []).length > 0 && (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}
        >
          {(d.step_ids ?? []).map((s) => (
            <span key={s} className="chip accent">
              {s}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
        {isRunning ? (
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: stalled ? "var(--danger)" : "var(--text-dim)",
              fontWeight: stalled ? 700 : undefined,
            }}
          >
            {elapsedLabel(d.created_at)}
            {d.estimated_duration_min
              ? ` / ~${d.estimated_duration_min}m est`
              : ""}
          </span>
        ) : (
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--text-dim)" }}
          >
            {timeAgo(d.created_at)}
          </span>
        )}
        {d.tool_call_count > 0 && (
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--text-dim)" }}
          >
            {d.tool_call_count} calls
          </span>
        )}
        {d.cost_usd != null && (
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--text-dim)" }}
          >
            {fmtCost(d.cost_usd)}
          </span>
        )}
        {!isRunning && d.duration_ms != null && (
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--text-dim)" }}
          >
            {durLabel(d.duration_ms)}
          </span>
        )}
        {d.droplet_id && (
          <span
            className="mono"
            style={{ fontSize: 10, color: "var(--text-dim)" }}
          >
            ◈ {d.droplet_ip ?? d.droplet_id}
          </span>
        )}
      </div>
    </button>
  );
}

function RecentRow({
  d,
  onTap,
}: {
  d: DispatchItem;
  onTap: () => void;
}) {
  const isOk = d.status === "complete" || d.status === "success";
  const isFail = d.status === "failed" || d.status === "error";
  return (
    <button
      onClick={onTap}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 20px",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        background: "transparent",
        border: "none",
        borderBottom: "1px solid var(--border-soft)",
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: isOk
            ? "var(--ok)"
            : isFail
              ? "var(--danger)"
              : "var(--text-dim)",
          flexShrink: 0,
          fontFamily: "var(--font-mono)",
        }}
      >
        {isOk ? "✓" : isFail ? "✕" : "·"}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: "var(--accent)",
          fontWeight: 700,
          flexShrink: 0,
          minWidth: 60,
        }}
      >
        {(d.persona_id ?? "?").toUpperCase().substring(0, 10)}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 10,
          color: "var(--text-muted)",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {d.job_id}
      </span>
      {d.duration_ms != null && (
        <span
          className="mono"
          style={{ fontSize: 10, color: "var(--text-dim)", flexShrink: 0 }}
        >
          {durLabel(d.duration_ms)}
        </span>
      )}
      {d.cost_usd != null ? (
        <span
          className="mono"
          style={{ fontSize: 10, color: "var(--text-dim)", flexShrink: 0 }}
        >
          {fmtCost(d.cost_usd)}
        </span>
      ) : (
        <span
          className="mono"
          style={{ fontSize: 10, color: "var(--text-dim)", flexShrink: 0 }}
        >
          —
        </span>
      )}
    </button>
  );
}

function eventBadgeColor(eventType: string): string {
  if (eventType.includes("complete") || eventType.includes("success"))
    return "var(--ok)";
  if (eventType.includes("fail") || eventType.includes("error"))
    return "var(--danger)";
  if (eventType.includes("start") || eventType.includes("dispatch"))
    return "var(--accent)";
  return "var(--text-dim)";
}

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function TimelineSection({
  timeline,
}: {
  timeline: DispatchTimelineResponse | null;
}) {
  if (!timeline) return null;

  const events = timeline.events ?? [];
  const childEvents = timeline.child_events ?? [];
  const hasEvents = events.length > 0 || childEvents.length > 0;

  if (!hasEvents) {
    return (
      <div className="sheet-section">
        <h4>Timeline</h4>
        <div
          style={{
            padding: "16px 10px",
            textAlign: "center",
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          No lifecycle events recorded
        </div>
      </div>
    );
  }

  type MergedEvent = {
    key: string;
    event_type: string;
    message: string | null;
    created_at: string;
    persona_id: string | null;
    isChild: boolean;
    child_job_id?: string;
    child_persona_id?: string | null;
  };

  const merged: MergedEvent[] = [
    ...events.map((e: TimelineEvent, i: number) => ({
      key: `p-${i}`,
      event_type: e.event_type,
      message: e.message,
      created_at: e.created_at,
      persona_id: e.persona_id,
      isChild: false,
    })),
    ...childEvents.map((e: ChildTimelineEvent, i: number) => ({
      key: `c-${i}`,
      event_type: e.event_type,
      message: e.message,
      created_at: e.created_at,
      persona_id: e.persona_id,
      isChild: true,
      child_job_id: e.child_job_id,
      child_persona_id: e.child_persona_id,
    })),
  ].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const cs = timeline.child_summary ?? { total: 0, success: 0, failed: 0, running: 0, total_cost_usd: 0, total_duration_ms: 0 };

  return (
    <div className="sheet-section">
      <h4>Timeline ({merged.length})</h4>
      {cs.total > 0 && (
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--text-muted)",
            padding: "6px 10px",
            background: "var(--bg-elev)",
            borderRadius: 8,
            border: "1px solid var(--border-soft)",
            marginBottom: 8,
          }}
        >
          {cs.total} {cs.total === 1 ? "child" : "children"}
          {cs.success > 0 ? `: ${cs.success} success` : ""}
          {cs.failed > 0 ? `, ${cs.failed} failed` : ""}
          {cs.running > 0 ? `, ${cs.running} running` : ""}
          {(cs.total_cost_usd ?? 0) > 0 ? ` — ${fmtCost(cs.total_cost_usd)}` : ""}
          {(cs.total_duration_ms ?? 0) > 0 ? `, ${durLabel(cs.total_duration_ms)}` : ""}
        </div>
      )}
      <div
        style={{
          background: "var(--bg-elev)",
          borderRadius: 8,
          border: "1px solid var(--border-soft)",
          overflow: "hidden",
        }}
      >
        {merged.map((ev) => (
          <div
            key={ev.key}
            style={{
              display: "flex",
              gap: 8,
              padding: "8px 10px",
              borderBottom: "1px solid var(--border-soft)",
              paddingLeft: ev.isChild ? 26 : 10,
            }}
          >
            <div
              style={{
                width: 4,
                borderRadius: 2,
                background: eventBadgeColor(ev.event_type),
                flexShrink: 0,
                alignSelf: "stretch",
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 2,
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: eventBadgeColor(ev.event_type),
                    fontWeight: 700,
                  }}
                >
                  {ev.event_type}
                </span>
                {ev.persona_id && (
                  <span
                    className="mono"
                    style={{ fontSize: 9, color: "var(--accent)" }}
                  >
                    {ev.persona_id.toUpperCase()}
                  </span>
                )}
                {ev.isChild && ev.child_persona_id && (
                  <span
                    className="mono"
                    style={{ fontSize: 9, color: "var(--text-dim)" }}
                  >
                    child:{ev.child_persona_id}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                <span
                  className="mono"
                  style={{ fontSize: 9, color: "var(--text-dim)", flexShrink: 0 }}
                >
                  {fmtTimestamp(ev.created_at)}
                </span>
              </div>
              {ev.message && (
                <div
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: "var(--text-muted)",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                  }}
                >
                  {ev.message}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailSheet({
  detail,
  loading,
  onClose,
  onCancel,
  cancelling,
  timeline,
  error,
}: {
  detail: DispatchDetail | null;
  loading: boolean;
  onClose: () => void;
  onCancel?: (id: string) => void;
  cancelling?: boolean;
  timeline: DispatchTimelineResponse | null;
  error?: string | null;
}) {
  const isOpen = detail !== null || loading || !!error;

  return (
    <>
      <div
        className={`sheet-backdrop${isOpen ? " open" : ""}`}
        style={{ position: "fixed" }}
        onClick={onClose}
      />
      <div
        className={`sheet${isOpen ? " open" : ""}`}
        style={{ position: "fixed" }}
      >
        <div className="sheet-handle" />
        {loading && !detail && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            Loading...
          </div>
        )}
        {!!error && !loading && !detail && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--danger)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            {error}
          </div>
        )}
        {detail && (
          <>
            <div className="sheet-head">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div className="eyebrow">
                  {(detail.persona_id ?? "unknown").toUpperCase()}
                </div>
                {(detail.status === "running" || detail.status === "pending") && onCancel && (
                  <button
                    style={{
                      fontSize: 10,
                      fontFamily: "var(--font-mono)",
                      color: "var(--danger)",
                      background: "var(--danger-soft)",
                      border: "1px solid var(--danger)",
                      borderRadius: 6,
                      padding: "3px 10px",
                      cursor: cancelling ? "not-allowed" : "pointer",
                      opacity: cancelling ? 0.5 : 1,
                    }}
                    disabled={cancelling}
                    onClick={() => onCancel(detail.job_id)}
                  >
                    {cancelling ? "Cancelling…" : "Dismiss"}
                  </button>
                )}
              </div>
              <h3
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  wordBreak: "break-all",
                }}
              >
                {detail.job_id}
              </h3>
            </div>
            <div className="sheet-body">
              <div className="sheet-section">
                <h4>Status</h4>
                <div className="sheet-row">
                  <span className="k">Status</span>
                  <span className="v">
                    <span className={statusChipClass(detail.status)}>
                      {detail.status}
                    </span>
                  </span>
                </div>
                <div className="sheet-row">
                  <span className="k">Started</span>
                  <span className="v mono" style={{ fontSize: 11 }}>
                    {new Date(detail.created_at).toLocaleString()}
                  </span>
                </div>
                {detail.status === "running" && (
                  <div className="sheet-row">
                    <span className="k">Elapsed</span>
                    <span className="v mono" style={{ fontSize: 11 }}>
                      {elapsedLabel(detail.created_at)}
                    </span>
                  </div>
                )}
                {detail.duration_ms != null && (
                  <div className="sheet-row">
                    <span className="k">Duration</span>
                    <span className="v">{durLabel(detail.duration_ms)}</span>
                  </div>
                )}
                {detail.estimated_duration_min != null && (
                  <div className="sheet-row">
                    <span className="k">Est. duration</span>
                    <span className="v">~{detail.estimated_duration_min}m</span>
                  </div>
                )}
                {detail.session_id && (
                  <div className="sheet-row">
                    <span className="k">Session</span>
                    <span className="v mono" style={{ fontSize: 11 }}>
                      {detail.session_id}
                    </span>
                  </div>
                )}
              </div>

              <div className="sheet-section">
                <h4>Progress</h4>
                {(detail.tool_call_count ?? 0) > 0 && (
                  <div className="sheet-row">
                    <span className="k">Tool calls</span>
                    <span className="v">{detail.tool_call_count}</span>
                  </div>
                )}
                {(detail.commits_pushed ?? 0) > 0 && (
                  <div className="sheet-row">
                    <span className="k">Commits</span>
                    <span className="v">{detail.commits_pushed}</span>
                  </div>
                )}
                {(detail.prs_created ?? 0) > 0 && (
                  <div className="sheet-row">
                    <span className="k">PRs created</span>
                    <span className="v">{detail.prs_created}</span>
                  </div>
                )}
                {(detail.prs_merged ?? 0) > 0 && (
                  <div className="sheet-row">
                    <span className="k">PRs merged</span>
                    <span className="v">{detail.prs_merged}</span>
                  </div>
                )}
                <div className="sheet-row">
                  <span className="k">Cost</span>
                  <span className="v">{fmtCost(detail.cost_usd)}</span>
                </div>
              </div>

              {detail.droplet_id && (
                <div className="sheet-section">
                  <h4>Droplet</h4>
                  <div className="sheet-row">
                    <span className="k">ID</span>
                    <span className="v mono" style={{ fontSize: 11 }}>
                      {detail.droplet_id}
                    </span>
                  </div>
                  {detail.droplet_ip && (
                    <div className="sheet-row">
                      <span className="k">IP</span>
                      <span className="v mono" style={{ fontSize: 11 }}>
                        {detail.droplet_ip}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {(detail.step_ids ?? []).length > 0 && (
                <div className="sheet-section">
                  <h4>Steps</h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(detail.step_ids ?? []).map((s) => (
                      <span key={s} className="chip accent">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {detail.track_id && (
                <div className="sheet-section">
                  <div className="sheet-row">
                    <span className="k">Track</span>
                    <span className="v mono" style={{ fontSize: 11 }}>
                      {detail.track_id}
                    </span>
                  </div>
                </div>
              )}

              {detail.parent_job_id && (
                <div className="sheet-section">
                  <div className="sheet-row">
                    <span className="k">Parent</span>
                    <span className="v mono" style={{ fontSize: 11 }}>
                      {detail.parent_job_id}
                    </span>
                  </div>
                </div>
              )}

              {(detail.children ?? []).length > 0 && (
                <div className="sheet-section">
                  <h4>Child dispatches ({(detail.children ?? []).length})</h4>
                  {(detail.children ?? []).map((c) => (
                    <div
                      key={c.job_id}
                      style={{
                        padding: "8px 10px",
                        background: "var(--bg-elev)",
                        borderRadius: 8,
                        marginBottom: 6,
                        border: "1px solid var(--border-soft)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 3,
                        }}
                      >
                        <span
                          className="mono"
                          style={{ fontSize: 10, color: "var(--accent)" }}
                        >
                          {(c.persona_id ?? "?").toUpperCase()}
                        </span>
                        <span style={{ flex: 1 }} />
                        <span className={statusChipClass(c.status)}>
                          {c.status}
                        </span>
                      </div>
                      <div
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: "var(--text-muted)",
                          wordBreak: "break-all",
                          marginBottom: 3,
                        }}
                      >
                        {c.job_id}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {(c.tool_call_count ?? 0) > 0 && (
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: "var(--text-dim)" }}
                          >
                            {c.tool_call_count} calls
                          </span>
                        )}
                        <span
                          className="mono"
                          style={{ fontSize: 10, color: "var(--text-dim)" }}
                        >
                          {fmtCost(c.cost_usd)}
                        </span>
                        {c.duration_ms != null && (
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: "var(--text-dim)" }}
                          >
                            {durLabel(c.duration_ms)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {detail.error && (
                <div className="sheet-section">
                  <h4>Error</h4>
                  <div
                    style={{
                      padding: "10px",
                      background: "var(--danger-soft)",
                      border: "1px solid var(--danger)",
                      borderRadius: 8,
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      color: "var(--danger)",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.5,
                    }}
                  >
                    {detail.error}
                  </div>
                </div>
              )}

              {detail.output && (
                <div className="sheet-section">
                  <h4>Output</h4>
                  <div
                    style={{
                      padding: "10px",
                      background: "var(--bg-elev)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 8,
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      color: "var(--text-muted)",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.5,
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    {detail.output}
                  </div>
                </div>
              )}

              <TimelineSection timeline={timeline} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

interface DispatchScreenProps {
  onNavigate?: (intent: string | NavigationIntent) => void;
}

export function DispatchScreen({ onNavigate: _onNavigate }: DispatchScreenProps = {}) {
  const [active, setActive] = useState<DispatchItem[]>([]);
  const [recent, setRecent] = useState<DispatchItem[]>([]);
  const [stats, setStats] = useState<DispatchStats>({
    total: 0,
    success: 0,
    failed: 0,
    running: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DispatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [timeline, setTimeline] = useState<DispatchTimelineResponse | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const res = await fetchDispatches();
      setActive(res.active ?? []);
      setRecent(res.recent ?? []);
      setStats(res.stats ?? { total: 0, success: 0, failed: 0, running: 0 });
    } catch {
      setLoadError('Failed to refresh dispatches.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 30_000);
    return () => clearInterval(interval);
  }, [load]);

  const openDetail = useCallback(async (id: string) => {
    setDetailError(null);
    setSelectedId(id);
    setDetail(null);
    setTimeline(null);
    setDetailLoading(true);
    try {
      const [d, t] = await Promise.all([
        fetchDispatchDetail(id),
        fetchDispatchTimeline(id).catch(() => null),
      ]);
      setDetail(d);
      setTimeline(t);
    } catch {
      setDetailError('Failed to load dispatch details. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
    setTimeline(null);
    setDetailError(null);
  }, []);

  const dismissDispatch = useCallback(async (id: string) => {
    if (cancellingId) return;
    if (!window.confirm("Cancel this dispatch? This will mark it as stale.")) return;
    setCancellingId(id);
    try {
      await cancelDispatch(id);
      setSelectedId(null);
      setDetail(null);
      setTimeline(null);
      void load();
    } catch {
      alert("Cancel failed. Please try again.");
    } finally {
      setCancellingId(null);
    }
  }, [load, cancellingId]);

  useEffect(() => {
    if (!selectedId) return;
    const running = active.find(
      (d) => d.job_id === selectedId && d.status === "running",
    );
    if (!running) return;
    const interval = setInterval(() => {
      void openDetail(selectedId);
    }, 30_000);
    return () => clearInterval(interval);
  }, [selectedId, active, openDetail]);

  // Build parent-child map from all known jobs
  const allJobs = [...active, ...recent];
  const childrenMap = new Map<string, DispatchItem[]>();
  for (const d of allJobs) {
    if (d.parent_job_id) {
      const arr = childrenMap.get(d.parent_job_id) ?? [];
      arr.push(d);
      childrenMap.set(d.parent_job_id, arr);
    }
  }

  const stalled = active.filter(isStalled);
  const normal = active.filter((d) => !isStalled(d));
  const activeIds = new Set(normal.map((d) => d.job_id));
  const rootNormal = normal.filter(
    (d) => !d.parent_job_id || !activeIds.has(d.parent_job_id),
  );

  // VLR-1155: render the full dispatch hierarchy recursively
  // (COS → planner → orchestrator → executor/reviewer), any depth.
  // The visited set guards against parent_job_id cycles in bad data.
  const renderDispatchTree = (
    d: DispatchItem,
    depth: number,
    visited: Set<string>,
  ): ReactNode => {
    if (visited.has(d.job_id)) return null;
    visited.add(d.job_id);
    return (
      <div key={d.job_id}>
        <DispatchCard
          d={d}
          onTap={() => void openDetail(d.job_id)}
          onDismiss={(id) => void dismissDispatch(id)}
          depth={depth}
        />
        {(childrenMap.get(d.job_id) ?? []).map((child) =>
          renderDispatchTree(child, depth + 1, visited),
        )}
      </div>
    );
  };

  return (
    <>
      <StatsBar stats={stats} />
      <PersonaBreakdown dispatches={active} />

      {stalled.length > 0 && (
        <>
          <div className="section-head">
            <h2>Stall Alerts</h2>
            <span className="count">{stalled.length}</span>
          </div>
          {stalled.map((d) => (
            <DispatchCard
              key={d.job_id}
              d={d}
              onTap={() => void openDetail(d.job_id)}
              onDismiss={(id) => void dismissDispatch(id)}
            />
          ))}
        </>
      )}

      {normal.length > 0 && (
        <>
          <div className="section-head">
            <h2>Active</h2>
            <span className="count">{normal.length}</span>
          </div>
          {(() => {
            const visited = new Set<string>();
            return rootNormal.map((d) => renderDispatchTree(d, 0, visited));
          })()}
        </>
      )}

      <div className="section-head">
        <h2>Recent</h2>
        <span className="count">{recent.length}</span>
      </div>

      {loading && (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          Loading...
        </div>
      )}

      {loadError && (
        <div
          style={{
            padding: '12px 20px',
            color: 'var(--color-danger, #e53e3e)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            textAlign: 'center',
          }}
        >
          {loadError}
        </div>
      )}

      {!loading && active.length === 0 && recent.length === 0 && (
        <div className="quiet-state">
          <div className="glyph">→</div>
          <h3>No dispatches</h3>
          <p>No activity in the last 24 hours.</p>
        </div>
      )}

      <div
        style={{
          background: "var(--bg-elev)",
          borderRadius: 12,
          border: "1px solid var(--border-soft)",
          margin: "0 20px",
          overflow: "hidden",
        }}
      >
        {recent.map((d) => (
          <RecentRow
            key={d.job_id}
            d={d}
            onTap={() => void openDetail(d.job_id)}
          />
        ))}
      </div>

      <div style={{ height: 80 }} />

      <DetailSheet
        detail={detail}
        loading={detailLoading}
        onClose={closeDetail}
        onCancel={(id) => void dismissDispatch(id)}
        cancelling={cancellingId === detail?.job_id}
        timeline={timeline}
        error={detailError}
      />
    </>
  );
}
